import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useKpi, useKpiTrend, useSummary } from '@/hooks/useDashboard';
import { KpiCard } from '@/components/features/dashboard/KpiCard';
import { KgiHero } from '@/components/features/dashboard/KgiHero';
import { CsQualitySection } from '@/components/features/dashboard/CsQualitySection';
import { AiSummaryCard } from '@/components/features/dashboard/AiSummaryCard';
import { ErrorState } from '@/components/feedback/ErrorState';
import {
  CrmPeriodToggle,
  PeriodToggle,
  type CsPeriod,
  type Period,
} from '@/components/ui/PeriodToggle';
import { TrendChart } from '@/components/charts/TrendChart';
import {
  aggregateTrend,
  aggregationLabel,
  latestPeriodLabel,
  latestValue,
  periodAxisLabel,
} from '@/lib/aggregate';
import { kpiDef } from '@/lib/kpi-definitions';
import { buildRuleSummary } from '@/lib/daily-summary';

/**
 * @graph-node {Page}
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business ダッシュボードページ。QAG KGI(退会率)をヒーロー表示し、CRM機能(退会者数・オンボ成功率・ロイヤル成功率)とCS機能(KPI=初動SLO10秒・回答SLO1分未満・3ターム解決率・解決率、モニタリング=AI完遂率・満足度)をドライバーとして並列配置する。期間トグルはセクション単位で、KGIヒーローが月次/四半期/年度、CRMが週次/月次/四半期/年度(3枚一括)、CSが週次/月次/四半期
 * @graph-connects useKpi [calls] KPIデータフック
 * @graph-connects useKpiTrend [calls] KPIトレンドデータフック
 * @graph-connects useSummary [calls] サマリーデータフック
 * @graph-connects KgiHero [calls] KGIヒーロー描画
 * @graph-connects KpiCard [calls] KPIカード描画
 * @graph-connects CsQualitySection [calls] CS対応品質セクション描画
 * @graph-connects AiSummaryCard [calls] AIサマリーカード描画
 * @graph-connects buildRuleSummary [calls] APIがサマリーを返さないときに規則ベースで組み立てる
 * @graph-connects PeriodToggle [calls] KGIヒーローの集計期間の切替
 * @graph-connects CrmPeriodToggle [calls] CRMセクション3枚の集計期間の一括切替
 * @graph-connects TrendChart [calls] 軸付きトレンド描画
 * @graph-connects ErrorState [calls] エラー表示描画
 */
export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

/** 増減の比較先ラベル。系列の集約単位と一致させ、粒度の誤読を防ぐ */
/** @graph-connects none */
const COMPARISON_LABEL: Record<Period, string> = {
  month: '前月比',
  quarter: '前四半期比',
  year: '前年度比',
};

/** @graph-connects none */
function formatPercent(value: number | null | undefined, digits = 1): string | null {
  if (value == null) return null;
  return (value * 100).toFixed(digits);
}

/** @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す */
function DashboardPage() {
  const [period, setPeriod] = useState<Period>('month');
  // CS セクションだけ週次まで刻める。他セクションは日次データを持たない
  const [csPeriod, setCsPeriod] = useState<CsPeriod>('week');
  // CRM セクションは3枚とも週次を持てるので、セクション単位で一括に切り替える。
  // 既定は月次。週次はコホート 500〜1,000 名で数 pt 振れ、基準値と見比べにくい。
  // オンボ（94日ラグ）とロイヤル（187日ラグ）で確定済みの週の範囲は違うが、
  // 値が無い週は点にしない作りなので、粒度を揃えても短い方が伸びたりはしない
  const [crmPeriod, setCrmPeriod] = useState<CsPeriod>('month');

  const kpiQuery = useKpi();
  // 月次系列（退会率・退会者数）なので2年分を取る。30日だと確定月が1つも揃わず
  // 前月比も四半期・年度トグルも成立しない
  const trendQuery = useKpiTrend(730);
  const summaryQuery = useSummary();

  const kpi = kpiQuery.data?.data ?? null;
  const trendItems = trendQuery.data?.data ?? [];
  // API は将来 Gemini 版を載せるための口で、いまは本番で常に null を返す。
  // 返ってこない間は、タイルと同じ値から規則で組み立てたものを出す
  // （`generated_by` でどちらが出ているか区別できる）
  const summary = summaryQuery.data?.data ?? buildRuleSummary(kpi, trendItems);

  const isLoading = kpiQuery.isLoading || trendQuery.isLoading;
  const hasError = kpiQuery.isError || trendQuery.isError;

  const xLabel = periodAxisLabel(period);
  const churnRateTrend = aggregateTrend(trendItems, 'churnRate', period);
  const churnCountTrend = aggregateTrend(trendItems, 'churnCount', crmPeriod);
  const onboardingTrend = aggregateTrend(trendItems, 'onboardingRate', crmPeriod);
  const loyaltyTrend = aggregateTrend(trendItems, 'loyaltyRate', crmPeriod);
  // 週の意味が指標で違うため、X軸ラベルは指標ごとに引く（登録週 / 退会の発生週）
  const crmChurnXLabel = periodAxisLabel(crmPeriod, 'churnCount');
  const crmCohortXLabel = periodAxisLabel(crmPeriod, 'onboardingRate');

  return (
    <div style={{ animation: 'fadeIn 0.4s ease' }}>
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            ダッシュボード
          </h1>
          <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
            対象日: {kpi?.date ?? '—'}
          </span>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>

      {hasError && (
        <ErrorState
          title="データ取得エラー"
          message="KPIデータの取得に失敗しました。"
          onRetry={() => {
            void kpiQuery.refetch();
            void trendQuery.refetch();
          }}
        />
      )}

      {!hasError && (
        <>
          {/* QAG グループ KGI ＝ 退会率（最上部ヒーロー） */}
          <KgiHero
            kgi={kpi?.kgi ?? null}
            month={kpi?.month ?? null}
            trend={churnRateTrend}
            xLabel={xLabel}
            aggregation={aggregationLabel('churnRate', period)}
            comparisonLabel={COMPARISON_LABEL[period]}
            isLoading={isLoading}
          />

          {/* チーム / CRM 機能 ＝ 退会者数 × オンボ成功率 × ロイヤル成功率 */}
          <section className="mb-6">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <h2
                  className="text-sm font-semibold"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  CRM ｜ ロイヤリティ改善
                </h2>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  退会率を動かすドライバー
                </span>
              </div>
              {/* 3枚とも週次を持てるので、セクション単位で一括に切り替える */}
              <CrmPeriodToggle value={crmPeriod} onChange={setCrmPeriod} />
            </div>
            <div className="bento-grid">
              {isLoading ? (
                <>
                  <KpiCardSkeleton />
                  <KpiCardSkeleton />
                  <KpiCardSkeleton />
                </>
              ) : (
                <>
                  {/* 数字はグラフと同じ系列の末尾から取る。/kpi スナップショットを直接読むと
                      期間トグルを切り替えても数字が動かない（2026-10-08 修正） */}
                  <KpiCard
                    title={kpiDef('churnCount').label}
                    period={latestPeriodLabel(churnCountTrend)}
                    value={latestValue(churnCountTrend)?.toLocaleString() ?? null}
                    unit="件"
                    chart={
                      <TrendChart
                        data={churnCountTrend}
                        yLabel={kpiDef('churnCount').metricName}
                        xLabel={crmChurnXLabel}
                        aggregation={aggregationLabel('churnCount', crmPeriod)}
                        unit="count"
                        color="var(--color-primary)"
                        height={176}
                      />
                    }
                  />
                  <KpiCard
                    title={kpiDef('onboardingRate').label}
                    period={latestPeriodLabel(onboardingTrend)}
                    value={formatPercent(latestValue(onboardingTrend))}
                    unit="%"
                    chart={
                      <TrendChart
                        data={onboardingTrend}
                        yLabel={kpiDef('onboardingRate').metricName}
                        xLabel={crmCohortXLabel}
                        aggregation={aggregationLabel('onboardingRate', crmPeriod)}
                        unit="percent"
                        color="var(--color-secondary)"
                        height={176}
                      />
                    }
                  />
                  <KpiCard
                    title={kpiDef('loyaltyRate').label}
                    period={latestPeriodLabel(loyaltyTrend)}
                    value={formatPercent(latestValue(loyaltyTrend))}
                    unit="%"
                    chart={
                      <TrendChart
                        data={loyaltyTrend}
                        yLabel={kpiDef('loyaltyRate').metricName}
                        xLabel={crmCohortXLabel}
                        aggregation={aggregationLabel('loyaltyRate', crmPeriod)}
                        unit="percent"
                        color="var(--color-success)"
                        height={176}
                      />
                    }
                  />
                </>
              )}
            </div>
            {kpi?.crm.onboardingCohortSize != null && (
              <p className="mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {kpiDef('onboardingRate').metricName}の分母（直近の確定コホート週{' '}
                {kpi.crm.onboardingCohortWeek ?? '—'}）{' '}
                {kpi.crm.onboardingCohortSize.toLocaleString()} 名 （
                {kpiDef('onboardingRate').definition}）
              </p>
            )}
            {kpi?.crm.loyaltyCohortSize != null && (
              <p className="mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {kpiDef('loyaltyRate').metricName}の分母（直近の確定コホート週{' '}
                {kpi.crm.loyaltyCohortWeek ?? '—'}） {kpi.crm.loyaltyCohortSize.toLocaleString()} 名
                （{kpiDef('loyaltyRate').definition}）
              </p>
            )}
          </section>

          {/* チーム / CS 機能 ＝ 状態ゴールを分解したKPI4本 と モニタリング2本（2026-09-29 整理） */}
          <CsQualitySection
            cs={kpi?.cs ?? null}
            csatTrend={aggregateTrend(trendItems, 'csat', csPeriod)}
            firstResponseTrend={aggregateTrend(trendItems, 'firstResponseSlaRate', csPeriod)}
            answerSlaTrend={aggregateTrend(trendItems, 'answerSlaRate', csPeriod)}
            threeTurnTrend={aggregateTrend(trendItems, 'threeTurnRate', csPeriod)}
            resolutionTrend={aggregateTrend(trendItems, 'resolutionRate', csPeriod)}
            aiCompletionTrend={aggregateTrend(trendItems, 'aiCompletionRate', csPeriod)}
            xLabel={periodAxisLabel(csPeriod)}
            csPeriod={csPeriod}
            onCsPeriodChange={setCsPeriod}
            isLoading={isLoading}
          />
        </>
      )}

      {/* AI サマリー */}
      <AiSummaryCard report={summary} isLoading={summaryQuery.isLoading || isLoading} />
    </div>
  );
}

/** KPI カードのスケルトン */
/** @graph-connects none */
function KpiCardSkeleton() {
  return (
    <div className="glass-card p-5">
      <div className="skeleton-shimmer h-3 w-20 rounded mb-3" />
      <div className="skeleton-shimmer h-8 w-24 rounded mb-2" />
      <div className="skeleton-shimmer h-12 w-full rounded mt-3" />
    </div>
  );
}
